import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { _get, _post, _put, _putForm } from "../../../libraries/fetch";
import {
  getLocalServerURL,
  getUpdateLicenseRequestsByBranchURL,
  saveUpdateLicenseRequestURL,
  updateUpdateLicenseRequestURL
} from "../../../libraries/apiUrl";
import { fetchBranchLicenseDetail, getLicenseExpiryDateString } from "../../../libraries/licenseExpiry";
import { getAdminUser, isLoggedInAdmin } from "../../../libraries/auth";
import { getActivityByName, withAppActivityFields } from "../../../libraries/activityLogger";
import { saveAppActivity } from "../../../libraries/tecgymAdminAppActivities";
import { showToast } from "../../../libraries/clipboardUtils";
import { getAllowedUpdateLicenseTabs } from "../../../libraries/privileges";

const LICENSE_TABS = [
  { id: "license", label: "License" },
  { id: "request", label: "License Update Request" },
  { id: "status", label: "Request Status" }
];

const panelStyle = {
  backgroundColor: "#2a2d35",
  border: "1px solid #444",
  borderRadius: "12px",
  padding: "clamp(16px, 3vw, 25px) clamp(14px, 3vw, 30px)",
  boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
};

const labelStyle = {
  display: "block",
  marginBottom: "10px",
  fontSize: "15px",
  fontWeight: 600,
  color: "#fff"
};

const inputStyle = {
  width: "100%",
  padding: "12px 15px",
  fontSize: "16px",
  backgroundColor: "#1e2127",
  border: "1px solid #444",
  borderRadius: "8px",
  color: "#fff",
  outline: "none",
  boxSizing: "border-box"
};

const primaryButtonStyle = {
  padding: "12px 28px",
  fontSize: "16px",
  fontWeight: 600,
  backgroundColor: "#187718",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer"
};

const headerRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  marginBottom: "20px",
  flexWrap: "wrap"
};

function UpdateLicense({
  companyId,
  branchId,
  branchName,
  branch,
  license = null,
  onLicenseRequestGenerated,
  onPendingLicenseRequestsChange
}) {
  const newExpiryDateInputRef = useRef(null);
  const allowedLicenseTabs = useMemo(() => getAllowedUpdateLicenseTabs(LICENSE_TABS), []);
  const [activeTab, setActiveTab] = useState(() => allowedLicenseTabs[0]?.id || "license");
  const [expiryDate, setExpiryDate] = useState("");
  const [isLoadingLicense, setIsLoadingLicense] = useState(false);
  const [licenseLoadError, setLicenseLoadError] = useState("");
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsLoadError, setRequestsLoadError] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestPopup, setShowRequestPopup] = useState(false);
  const [requestType, setRequestType] = useState("");
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const [extensionComment, setExtensionComment] = useState("");
  const [updateComment, setUpdateComment] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState("");
  const [showInvoiceLargeView, setShowInvoiceLargeView] = useState(false);
  const [showApprovalPopup, setShowApprovalPopup] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [requestInvoicePreview, setRequestInvoicePreview] = useState(null);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [isApprovingRequest, setIsApprovingRequest] = useState(false);
  const [isDeletingRequest, setIsDeletingRequest] = useState(false);
  const deletedRequestIdsRef = useRef(new Set());

  const normalizedBranchId = useMemo(() => resolveBranchId(branchId, branch), [branchId, branch]);
  const normalizedCompanyId = useMemo(() => resolveCompanyId(companyId, branch), [companyId, branch]);

  const normalizedLicense = useMemo(
    () => normalizeBranchLicense(license || branch?.license),
    [license, branch]
  );

  const fetchLicenseRequests = useCallback(async () => {
    if (!normalizedBranchId) {
      setLicenseRequests([]);
      return [];
    }

    setLoadingRequests(true);
    setRequestsLoadError("");

    try {
      const response = await _get(getUpdateLicenseRequestsByBranchURL(normalizedBranchId), {
        useCache: false
      });

      if (response === null) {
        throw new Error("Failed to load license update requests.");
      }

      if (response?.success === false || response?.status === false) {
        throw new Error(getApiResponseMessage(response) || "Failed to load license update requests.");
      }

      const branchRequests = resolveLicenseRequestsFromResponse(
        response,
        normalizedCompanyId,
        normalizedBranchId
      ).filter((request) => !deletedRequestIdsRef.current.has(String(request?.id || "")));

      setLicenseRequests(branchRequests);
      return branchRequests;
    } catch (error) {
      const errorMessage = error.message || "Failed to load license update requests.";
      setRequestsLoadError(errorMessage);
      setLicenseRequests([]);
      showToast(errorMessage, "error");
      return [];
    } finally {
      setLoadingRequests(false);
    }
  }, [normalizedBranchId, normalizedCompanyId]);

  useEffect(() => {
    fetchLicenseRequests();
  }, [fetchLicenseRequests]);

  const pendingRequests = useMemo(
    () =>
      licenseRequests.filter(
        (request) => !isDeletedStatus(request.status) && !isResolvedStatus(request.status)
      ),
    [licenseRequests]
  );

  const approvedRequests = useMemo(
    () =>
      licenseRequests.filter(
        (request) => !isDeletedStatus(request.status) && isResolvedStatus(request.status)
      ),
    [licenseRequests]
  );

  useEffect(() => {
    if (typeof onPendingLicenseRequestsChange !== "function") {
      return;
    }

    const pendingKeys = [
      ...new Set(pendingRequests.map((request) => String(request?.id || "")).filter(Boolean))
    ];

    onPendingLicenseRequestsChange(pendingKeys, pendingRequests.length);
  }, [onPendingLicenseRequestsChange, pendingRequests]);

  useEffect(() => {
    if (allowedLicenseTabs.length > 0 && !allowedLicenseTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(allowedLicenseTabs[0].id);
    }
  }, [activeTab, allowedLicenseTabs]);

  const fetchExpiryDate = useCallback(async () => {
    if (!normalizedBranchId) {
      setExpiryDate("");
      return;
    }

    setIsLoadingLicense(true);
    setLicenseLoadError("");

    try {
      const licenseDetail = await fetchBranchLicenseDetail(normalizedBranchId);
      const expiryValue = getLicenseExpiryDateString(licenseDetail);
      const nextExpiryDate = expiryValue
        ? new Date(expiryValue).toISOString().split("T")[0]
        : "";

      setExpiryDate(nextExpiryDate);
    } catch (error) {
      const errorMessage = error.message || "Failed to load license details.";
      setExpiryDate("");
      setLicenseLoadError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setIsLoadingLicense(false);
    }
  }, [normalizedBranchId]);

  useEffect(() => {
    fetchExpiryDate();
  }, [fetchExpiryDate]);

  const syncAndNotifyLicenseRequestGenerated = useCallback(
    async (shouldNotify = true) => {
      const refreshedRequests = await fetchLicenseRequests();
      const pendingKeys = [
        ...new Set(
          (Array.isArray(refreshedRequests) ? refreshedRequests : [])
            .filter((request) => !isResolvedStatus(request.status))
            .map((request) => String(request?.id || ""))
            .filter(Boolean)
        )
      ];

      if (typeof onPendingLicenseRequestsChange === "function") {
        onPendingLicenseRequestsChange(pendingKeys, pendingKeys.length);
      }

      if (shouldNotify && typeof onLicenseRequestGenerated === "function") {
        onLicenseRequestGenerated();
      }
    },
    [fetchLicenseRequests, onLicenseRequestGenerated, onPendingLicenseRequestsChange]
  );

  useEffect(() => {
    if (!invoiceFile || !invoiceFile.type.startsWith("image/")) {
      setInvoicePreviewUrl("");
      return;
    }

    const previewUrl = URL.createObjectURL(invoiceFile);
    setInvoicePreviewUrl(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [invoiceFile]);

  const formattedExpiryDate = useMemo(() => {
    if (isLoadingLicense) {
      return "Loading...";
    }

    if (!expiryDate) {
      return "N/A";
    }

    return formatDisplayDate(expiryDate);
  }, [expiryDate, isLoadingLicense]);

  const remainingDaysText = useMemo(() => {
    if (isLoadingLicense) {
      return "Loading...";
    }

    if (!expiryDate) {
      return "N/A";
    }

    const now = new Date().getTime();
    const futureDate = new Date(expiryDate).getTime();
    const daysLeft = Math.floor((futureDate - now) / (1000 * 60 * 60 * 24));

    return daysLeft < 0 ? "0 days" : `${daysLeft} days`;
  }, [expiryDate, isLoadingLicense]);

  const currentDate = useMemo(() => new Date().toISOString().split("T")[0], []);
  const formattedNewExpiryDate = useMemo(() => {
    return newExpiryDate ? formatDisplayDate(newExpiryDate) : "";
  }, [newExpiryDate]);
  const generatedBy = useMemo(() => {
    const adminUser = getAdminUser();
    return getActivityByName() || adminUser?.email || adminUser?.loginId || "Admin";
  }, []);
  const canDeleteRequests = useMemo(() => isLoggedInAdmin(), []);

  const canGenerateRequest = useMemo(() => {
    if (!requestType) {
      return false;
    }

    if (requestType === "expire") {
      return Boolean(normalizedLicense?.licenseKey);
    }

    if (!newExpiryDate) {
      return false;
    }

    if (requestType === "update") {
      return Boolean(invoiceFile);
    }

    return Boolean(extensionComment.trim());
  }, [extensionComment, invoiceFile, newExpiryDate, normalizedLicense, requestType]);

  const openRequestPopup = (nextRequestType = "") => {
    setRequestType(nextRequestType);
    setNewExpiryDate("");
    setExtensionComment("");
    setUpdateComment("");
    setInvoiceFile(null);
    setShowInvoiceLargeView(false);
    setShowRequestPopup(true);
  };

  const handleUpdateLicenseClick = () => {
    openRequestPopup("");
  };

  const handleExpireLicenseClick = () => {
    openRequestPopup("expire");
  };

  const handleCloseRequestPopup = () => {
    setShowInvoiceLargeView(false);
    setShowRequestPopup(false);
  };

  const handleRequestTypeChange = (nextRequestType) => {
    setRequestType(nextRequestType);
    setInvoiceFile(null);
    setShowInvoiceLargeView(false);
    setExtensionComment("");
    setUpdateComment("");
  };

  const handleNewExpiryDateClick = () => {
    if (!newExpiryDateInputRef.current) {
      return;
    }

    if (typeof newExpiryDateInputRef.current.showPicker === "function") {
      try {
        newExpiryDateInputRef.current.showPicker();
      } catch {
        newExpiryDateInputRef.current.click();
      }
      return;
    }

    newExpiryDateInputRef.current.click();
  };

  const handleGenerateRequest = async (event) => {
    event.preventDefault();

    if (!canGenerateRequest || !normalizedBranchId || !normalizedCompanyId || isSubmittingRequest) {
      return;
    }

    setIsSubmittingRequest(true);

    try {
      const requestedAt = new Date().toISOString();
      const normalizedRequestType = normalizeRequestType(requestType);
      const status = "pending";
      const isExpireRequest = requestType === "expire";

      const activityOptions = {
        activityType: "Add License Update Request",
        initiatedFrom: "Update License",
        name: branchName || normalizedBranchId,
        previousValue: { existingExpiryDate: expiryDate },
        updatedValue: {
          requestType: normalizedRequestType,
          status,
          ...(isExpireRequest
            ? {
                licenseKey: normalizedLicense?.licenseKey || ""
              }
            : {
                newExpiryDate
              }),
          ...(requestType === "extension" ? { extensionComment: extensionComment.trim() } : {}),
          ...(requestType === "update"
            ? {
                updateComment: updateComment.trim(),
                // Keep extensionComment in sync so comment is visible even if
                // an older API build does not yet persist updateComment.
                ...(updateComment.trim() ? { extensionComment: updateComment.trim() } : {})
              }
            : {})
        }
      };

      const basePayload = {
        companyId: normalizedCompanyId,
        branchId: normalizedBranchId,
        company: normalizedCompanyId,
        branch: normalizedBranchId,
        requestedAt,
        generatedBy,
        existingExpiryDate: expiryDate,
        requestType: normalizedRequestType,
        status,
        ...(isExpireRequest
          ? {
              license: normalizedLicense,
              licenseKey: normalizedLicense?.licenseKey || ""
            }
          : {
              newExpiryDate
            }),
        ...(requestType === "extension" ? { extensionComment: extensionComment.trim() } : {}),
        ...(requestType === "update"
          ? {
              updateComment: updateComment.trim(),
              ...(updateComment.trim() ? { extensionComment: updateComment.trim() } : {})
            }
          : {})
      };

      let response;

      if (requestType === "update" && invoiceFile) {
        response = await _post(
          saveUpdateLicenseRequestURL(normalizedBranchId),
          withAppActivityFields(basePayload, activityOptions)
        );

        if (response?.status === false || response?.success === false || response === null) {
          const errorMessage = getApiResponseMessage(response) || "Failed to create license update request.";
          showToast(errorMessage, "error");
          return;
        }

        const createdRequest =
          response?.updateLicenseRequest ||
          response?.data?.updateLicenseRequest ||
          response?.data ||
          response;
        const requestId = getRecordId(createdRequest?._id || createdRequest?.id);

        if (!requestId) {
          showToast("Request created, but invoice could not be uploaded (missing request id).", "error");
          setShowRequestPopup(false);
          setActiveTab("request");
          await syncAndNotifyLicenseRequestGenerated(true);
          return;
        }

        const uploadFile = await buildInvoiceUploadFile(invoiceFile);
        const formData = new FormData();
        formData.append("_id", requestId);
        formData.append("id", requestId);
        formData.append("company", normalizedCompanyId);
        formData.append("branch", normalizedBranchId);
        formData.append("companyId", normalizedCompanyId);
        formData.append("branchId", normalizedBranchId);
        formData.append("isThumb", "true");
        formData.append("invoiceImage", uploadFile, uploadFile.name);
        formData.append("requestType", "Update");
        if (updateComment.trim()) {
          formData.append("updateComment", updateComment.trim());
          formData.append("extensionComment", updateComment.trim());
        }

        const uploadResponse = await _putForm(
          updateUpdateLicenseRequestURL(normalizedBranchId),
          formData
        );

        if (
          uploadResponse?.status === false ||
          uploadResponse?.success === false ||
          uploadResponse === null
        ) {
          showToast(
            getApiResponseMessage(uploadResponse) ||
              "Request created, but invoice image failed to upload.",
            "error"
          );
          setShowRequestPopup(false);
          setActiveTab("request");
          await syncAndNotifyLicenseRequestGenerated(true);
          return;
        }
      } else {
        response = await _post(
          saveUpdateLicenseRequestURL(normalizedBranchId),
          withAppActivityFields(basePayload, activityOptions)
        );

        if (response?.status === false || response?.success === false || response === null) {
          const errorMessage = getApiResponseMessage(response) || "Failed to create license update request.";
          showToast(errorMessage, "error");
          return;
        }
      }

      setShowRequestPopup(false);
      setActiveTab("request");
      await syncAndNotifyLicenseRequestGenerated(true);
      await saveAppActivity(activityOptions);
      showToast("License update request generated successfully.", "success");
    } catch (error) {
      showToast(error.message || "Failed to create license update request.", "error");
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleOpenApprovalPopup = (request) => {
    if (!request || isApprovedStatus(request.status)) {
      return;
    }

    setSelectedRequest(request);
    setShowApprovalPopup(true);
  };

  const handleCloseApprovalPopup = () => {
    setShowApproveConfirm(false);
    setShowApprovalPopup(false);
    setSelectedRequest(null);
  };

  const handleOpenApproveConfirm = (event) => {
    event.preventDefault();
    setShowApproveConfirm(true);
  };

  const handleConfirmApproval = async () => {
    if (!selectedRequest?.id || isApprovingRequest) {
      return;
    }

    const isExpireRequest = isExpireRequestType(selectedRequest.requestType);

    if (!normalizedLicense?.licenseKey) {
      showToast("License information is not available for this branch.", "error");
      return;
    }

    if (!isExpireRequest && !selectedRequest.newExpiryDate) {
      showToast("New expiry date is missing for this request.", "error");
      return;
    }

    setIsApprovingRequest(true);

    try {
      const activityOptions = {
        activityType: "Approve License Update Request",
        initiatedFrom: "Update License",
        name: branchName || normalizedBranchId,
        previousValue: {
          existingExpiryDate: selectedRequest.existingExpiryDate || expiryDate,
          status: selectedRequest.status || "pending"
        },
        updatedValue: {
          ...(isExpireRequest ? { status: "Expire" } : { newExpiryDate: selectedRequest.newExpiryDate, status: "approved" }),
          approvedBy: generatedBy,
          ...(isUpdateRequestType(selectedRequest.requestType) && selectedRequest.updateComment
            ? { updateComment: selectedRequest.updateComment }
            : {}),
          ...(isExtensionRequestType(selectedRequest.requestType) && selectedRequest.extensionComment
            ? { extensionComment: selectedRequest.extensionComment }
            : {})
        }
      };

      const response = await _put(updateUpdateLicenseRequestURL(normalizedBranchId), {
        _id: selectedRequest.id,
        id: selectedRequest.id,
        companyId: normalizedCompanyId,
        branchId: normalizedBranchId,
        license: normalizedLicense,
        licenseKey: normalizedLicense.licenseKey,
        requestType: selectedRequest.requestType,
        ...(isExpireRequest ? {} : { newExpiryDate: selectedRequest.newExpiryDate }),
        status: isExpireRequest ? "Expire" : "approved",
        approvedBy: generatedBy,
        ...(isUpdateRequestType(selectedRequest.requestType) &&
        (selectedRequest.updateComment || selectedRequest.extensionComment)
          ? {
              updateComment:
                selectedRequest.updateComment || selectedRequest.extensionComment,
              extensionComment:
                selectedRequest.updateComment || selectedRequest.extensionComment
            }
          : {}),
        ...(isExtensionRequestType(selectedRequest.requestType) && selectedRequest.extensionComment
          ? { extensionComment: selectedRequest.extensionComment }
          : {})
      });

      if (response?.status === false || response?.success === false || response === null) {
        showToast(
          getApiResponseMessage(response) || "Failed to approve license update request.",
          "error"
        );
        return;
      }

      setShowApproveConfirm(false);
      setShowApprovalPopup(false);
      setSelectedRequest(null);
      setActiveTab("status");
      await Promise.all([fetchLicenseRequests(), fetchExpiryDate(), saveAppActivity(activityOptions)]);
      showToast("License update request approved successfully.", "success");
    } catch (error) {
      showToast(error.message || "Failed to approve license update request.", "error");
    } finally {
      setIsApprovingRequest(false);
    }
  };

  const handleOpenDeleteConfirm = (request) => {
    if (!request?.id || !canDeleteRequests) {
      return;
    }

    setRequestToDelete(request);
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setRequestToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!requestToDelete?.id || !normalizedBranchId || isDeletingRequest) {
      return;
    }

    setIsDeletingRequest(true);

    try {
      const activityOptions = {
        activityType: "Delete License Update Request",
        initiatedFrom: "Update License",
        name: branchName || normalizedBranchId,
        previousValue: {
          requestType: requestToDelete.requestType,
          status: requestToDelete.status,
          requestedAt: requestToDelete.requestedAt
        },
        updatedValue: {
          deleted: true
        }
      };

      const response = await _put(
        updateUpdateLicenseRequestURL(normalizedBranchId),
        withAppActivityFields(
          {
            _id: requestToDelete.id,
            id: requestToDelete.id,
            companyId: normalizedCompanyId,
            branchId: normalizedBranchId,
            isDelete: true,
            status: "deleted"
          },
          activityOptions
        )
      );

      if (response?.status === false || response?.success === false || response === null) {
        showToast(
          getApiResponseMessage(response) || "Failed to delete license update request.",
          "error"
        );
        return;
      }

      const deletedId = String(requestToDelete.id);
      deletedRequestIdsRef.current.add(deletedId);
      setLicenseRequests((current) =>
        current.filter((request) => String(request?.id || "") !== deletedId)
      );
      handleCloseDeleteConfirm();
      await syncAndNotifyLicenseRequestGenerated(false);
      setLicenseRequests((current) =>
        current.filter(
          (request) =>
            String(request?.id || "") !== deletedId &&
            !deletedRequestIdsRef.current.has(String(request?.id || "")) &&
            !isDeletedStatus(request?.status)
        )
      );
      await saveAppActivity(activityOptions);
      showToast("License update request deleted successfully.", "success");
    } catch (error) {
      showToast(error.message || "Failed to delete license update request.", "error");
    } finally {
      setIsDeletingRequest(false);
    }
  };

  return (
    <div className="configMain">
      <div style={headerRowStyle}>
        <h1 style={{ margin: 0 }}>Update License</h1>
      </div>

      {isLoadingLicense && (
        <div style={{ marginBottom: "10px", color: "#9ca3af", fontSize: "13px" }}>
          Loading license details...
        </div>
      )}

      {!isLoadingLicense && licenseLoadError && (
        <div style={{ marginBottom: "10px", color: "#f87171", fontSize: "13px" }}>
          {licenseLoadError}
        </div>
      )}

      {!isLoadingLicense && requestsLoadError && (
        <div style={{ marginBottom: "10px", color: "#f87171", fontSize: "13px" }}>
          {requestsLoadError}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginBottom: "20px"
        }}
      >
        {allowedLicenseTabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const showPendingBadge = tab.id === "request" && pendingRequests.length > 0;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: isActive ? "1px solid #187718" : "1px solid #444",
                backgroundColor: isActive ? "#187718" : "#2a2d35",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center"
              }}
            >
              {tab.label}
              {showPendingBadge && (
                <span
                  style={licenseRequestBadgeStyle}
                  aria-label={`${pendingRequests.length} pending license update requests`}
                >
                  {pendingRequests.length > 99 ? "99+" : pendingRequests.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === "license" && (
        <div className="license-panel" style={panelStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: "20px",
              flexWrap: "wrap",
              marginBottom: "22px"
            }}
          >
            <div style={{ minWidth: "min(240px, 100%)", flex: "1 1 220px" }}>
              <label style={labelStyle}>Remaining Days</label>
              <input type="text" readOnly value={remainingDaysText} style={inputStyle} />
            </div>
            <div style={{ minWidth: "min(240px, 100%)", flex: "1 1 220px" }}>
              <label style={labelStyle}>License Expiry Date</label>
              <input type="text" readOnly value={formattedExpiryDate} style={inputStyle} />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap"
            }}
          >
            <button
              type="button"
              onClick={handleUpdateLicenseClick}
              disabled={isLoadingLicense || isSubmittingRequest || !normalizedBranchId || !normalizedCompanyId}
              style={{
                ...primaryButtonStyle,
                width: "fit-content",
                opacity: isLoadingLicense || isSubmittingRequest ? 0.7 : 1,
                cursor: isLoadingLicense || isSubmittingRequest ? "not-allowed" : "pointer"
              }}
            >
              Update License
            </button>
            <button
              type="button"
              onClick={handleExpireLicenseClick}
              disabled={isLoadingLicense || isSubmittingRequest || !normalizedBranchId || !normalizedCompanyId}
              style={{
                ...primaryButtonStyle,
                backgroundColor: "#dc2626",
                width: "fit-content",
                opacity: isLoadingLicense || isSubmittingRequest ? 0.7 : 1,
                cursor: isLoadingLicense || isSubmittingRequest ? "not-allowed" : "pointer"
              }}
            >
              Expire
            </button>
          </div>
        </div>
      )}

      {activeTab === "request" && (
        <div style={panelStyle}>
          <h2 style={{ margin: "0 0 15px", color: "#fff" }}>License Update Request</h2>
          {loadingRequests ? (
            <div style={{ color: "#ccc", padding: "18px" }}>Loading license update requests...</div>
          ) : pendingRequests.length > 0 ? (
            <LicenseRequestTable
              requests={pendingRequests}
              onApproveClick={handleOpenApprovalPopup}
              onInvoicePreviewClick={setRequestInvoicePreview}
              canDelete={canDeleteRequests}
              onDeleteClick={handleOpenDeleteConfirm}
            />
          ) : (
            <div
              style={{
                color: "#ccc",
                backgroundColor: "#1e2127",
                border: "1px solid #444",
                borderRadius: "8px",
                padding: "18px"
              }}
            >
              There is no License Update Request.
            </div>
          )}
        </div>
      )}

      {activeTab === "status" && (
        <div style={panelStyle}>
          <h2 style={{ margin: "0 0 25px", color: "#fff" }}>Request Status</h2>
          {loadingRequests ? (
            <div style={{ color: "#ccc", padding: "18px" }}>Loading request status...</div>
          ) : approvedRequests.length > 0 ? (
            <LicenseRequestTable
              requests={approvedRequests}
              onApproveClick={handleOpenApprovalPopup}
              onInvoicePreviewClick={setRequestInvoicePreview}
              canDelete={canDeleteRequests}
              onDeleteClick={handleOpenDeleteConfirm}
            />
          ) : (
            <div
              style={{
                color: "#ccc",
                backgroundColor: "#1e2127",
                border: "1px solid #444",
                borderRadius: "8px",
                padding: "18px"
              }}
            >
              No approved license update request yet.
            </div>
          )}
        </div>
      )}

      {showRequestPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px"
          }}
          onClick={handleCloseRequestPopup}
        >
          <form
            onSubmit={handleGenerateRequest}
            style={{
              ...panelStyle,
              width: "min(520px, 100%)",
              maxWidth: "100%",
              boxSizing: "border-box"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 25px", color: "#fff", textAlign: "center" }}>
              License Update Request
            </h2>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>Current Date</label>
              <input type="text" readOnly value={formatDisplayDate(currentDate)} style={inputStyle} />
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>Existing Expiry Date</label>
              <input type="text" readOnly value={formattedExpiryDate} style={inputStyle} />
            </div>

            {requestType === "expire" ? (
              <div style={{ marginBottom: "18px" }}>
                <label style={labelStyle}>Status</label>
                <input type="text" readOnly value="Expire" style={inputStyle} />
              </div>
            ) : (
              <div style={{ marginBottom: "18px" }}>
                <label style={labelStyle}>New Expiry Date</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    readOnly
                    value={formattedNewExpiryDate}
                    placeholder="Select Date"
                    onClick={handleNewExpiryDateClick}
                    style={{
                      ...inputStyle,
                      paddingRight: "48px",
                      cursor: "pointer"
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleNewExpiryDateClick}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </button>
                  <input
                    ref={newExpiryDateInputRef}
                    type="date"
                    value={newExpiryDate}
                    onChange={(event) => setNewExpiryDate(event.target.value)}
                    style={{
                      position: "absolute",
                      right: 0,
                      bottom: 0,
                      width: "1px",
                      height: "1px",
                      opacity: 0,
                      pointerEvents: "none"
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>Request Type</label>
              <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", justifyContent: "center" }}>
                <label style={{ color: "#fff", display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={requestType === "update"}
                    onChange={() => handleRequestTypeChange("update")}
                  />
                  Update
                </label>
                <label style={{ color: "#fff", display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={requestType === "extension"}
                    onChange={() => handleRequestTypeChange("extension")}
                  />
                  Extension
                </label>
                <label style={{ color: "#fff", display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={requestType === "expire"}
                    onChange={() => handleRequestTypeChange("expire")}
                  />
                  Expire
                </label>
              </div>
            </div>

            {requestType === "update" && (
              <div style={{ marginBottom: "22px" }}>
                <label style={labelStyle}>Invoice</label>
                {invoicePreviewUrl && (
                  <div
                    style={{
                      backgroundColor: "#1e2127",
                      border: "1px solid #444",
                      borderRadius: "8px",
                      padding: "10px",
                      marginBottom: "14px",
                      textAlign: "center"
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowInvoiceLargeView(true)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer"
                      }}
                    >
                      <img
                        src={invoicePreviewUrl}
                        alt="Invoice preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "180px",
                          objectFit: "contain",
                          borderRadius: "6px"
                        }}
                      />
                    </button>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <label
                    style={{
                      ...primaryButtonStyle,
                      display: "inline-block"
                    }}
                  >
                    {invoiceFile ? "Change Invoice" : "Upload Invoice"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => setInvoiceFile(event.target.files?.[0] || null)}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
                <div style={{ marginTop: "18px" }}>
                  <label style={labelStyle}>Comment</label>
                  <textarea
                    value={updateComment}
                    onChange={(event) => setUpdateComment(event.target.value)}
                    placeholder="Enter update comment"
                    rows={3}
                    style={{
                      ...inputStyle,
                      resize: "vertical"
                    }}
                  />
                </div>
              </div>
            )}

            {requestType === "extension" && (
              <div style={{ marginBottom: "22px" }}>
                <label style={labelStyle}>Comment</label>
                <textarea
                  value={extensionComment}
                  onChange={(event) => setExtensionComment(event.target.value)}
                  placeholder="Enter extension comment"
                  rows={3}
                  style={{
                    ...inputStyle,
                    resize: "vertical"
                  }}
                />
              </div>
            )}

            <div style={{ marginBottom: "22px" }}>
              <label style={labelStyle}>Generated By</label>
              <input type="text" readOnly value={generatedBy} style={inputStyle} />
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleCloseRequestPopup}
                style={{
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: 600,
                  backgroundColor: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canGenerateRequest || isSubmittingRequest}
                style={{
                  ...primaryButtonStyle,
                  backgroundColor: canGenerateRequest && !isSubmittingRequest ? "#187718" : "#555",
                  cursor: canGenerateRequest && !isSubmittingRequest ? "pointer" : "not-allowed",
                  opacity: canGenerateRequest && !isSubmittingRequest ? 1 : 0.65
                }}
              >
                {isSubmittingRequest ? "Generating..." : "Generate Update Request"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showApprovalPopup && selectedRequest && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2400,
            padding: "20px"
          }}
          onClick={handleCloseApprovalPopup}
        >
          <form
            onSubmit={handleOpenApproveConfirm}
            style={{
              ...panelStyle,
              width: "min(480px, 100%)",
              maxWidth: "100%",
              boxSizing: "border-box"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 25px", color: "#fff", textAlign: "center" }}>
              {isExpireRequestType(selectedRequest.requestType)
                ? "Approve Expire License Request"
                : "Approve License Request"}
            </h2>

            <div style={{ marginBottom: "18px" }}>
              <label style={labelStyle}>Existing Expiry Date</label>
              <input
                type="text"
                readOnly
                value={formatDisplayDate(selectedRequest.currentExpiryDate)}
                style={inputStyle}
              />
            </div>

            {isExpireRequestType(selectedRequest.requestType) ? (
              <div style={{ marginBottom: "18px" }}>
                <label style={labelStyle}>Status</label>
                <input type="text" readOnly value="Expire" style={inputStyle} />
              </div>
            ) : (
              <div style={{ marginBottom: "18px" }}>
                <label style={labelStyle}>New Expiry Date</label>
                <input
                  type="text"
                  readOnly
                  value={formatDisplayDate(selectedRequest.newExpiryDate)}
                  style={inputStyle}
                />
              </div>
            )}

            <div style={{ marginBottom: "24px" }}>
              <label style={labelStyle}>Approved By</label>
              <input type="text" readOnly value={generatedBy} style={inputStyle} />
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleCloseApprovalPopup}
                style={{
                  padding: "12px 24px",
                  fontSize: "16px",
                  fontWeight: 600,
                  backgroundColor: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button type="submit" style={primaryButtonStyle}>
                Approve
              </button>
            </div>
          </form>
        </div>
      )}

      {showApproveConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.78)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2600,
            padding: "20px"
          }}
          onClick={() => setShowApproveConfirm(false)}
        >
          <div
            style={{
              ...panelStyle,
              width: "min(380px, 100%)",
              maxWidth: "100%",
              textAlign: "center",
              boxSizing: "border-box"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 12px", color: "#fff" }}>Confirm Approval</h2>
            <p style={{ margin: "0 0 24px", color: "#ccc", lineHeight: 1.5 }}>
              Are you sure you want to approve this license update request?
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleConfirmApproval}
                disabled={isApprovingRequest}
                style={{
                  ...primaryButtonStyle,
                  padding: "10px 28px",
                  opacity: isApprovingRequest ? 0.7 : 1,
                  cursor: isApprovingRequest ? "not-allowed" : "pointer"
                }}
              >
                {isApprovingRequest ? "Approving..." : "Yes"}
              </button>
              <button
                type="button"
                onClick={() => setShowApproveConfirm(false)}
                style={{
                  padding: "10px 28px",
                  fontSize: "16px",
                  fontWeight: 600,
                  backgroundColor: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && requestToDelete && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.78)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2700,
            padding: "20px"
          }}
          onClick={handleCloseDeleteConfirm}
        >
          <div
            style={{
              ...panelStyle,
              width: "min(380px, 100%)",
              maxWidth: "100%",
              textAlign: "center",
              boxSizing: "border-box"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 12px", color: "#fff" }}>Confirm Delete</h2>
            <p style={{ margin: "0 0 24px", color: "#ccc", lineHeight: 1.5 }}>
              Are you sure you want to delete this license update request?
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeletingRequest}
                style={{
                  ...primaryButtonStyle,
                  backgroundColor: "#dc2626",
                  padding: "10px 28px",
                  opacity: isDeletingRequest ? 0.7 : 1,
                  cursor: isDeletingRequest ? "not-allowed" : "pointer"
                }}
              >
                {isDeletingRequest ? "Deleting..." : "Yes"}
              </button>
              <button
                type="button"
                onClick={handleCloseDeleteConfirm}
                style={{
                  padding: "10px 28px",
                  fontSize: "16px",
                  fontWeight: 600,
                  backgroundColor: "#444",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {requestInvoicePreview && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.88)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2800,
            padding: "16px"
          }}
          onClick={() => setRequestInvoicePreview(null)}
        >
          <div
            style={{
              ...panelStyle,
              width: "96vw",
              height: "96vh",
              maxWidth: "96vw",
              maxHeight: "96vh",
              boxSizing: "border-box",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              padding: "20px 24px"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                marginBottom: "16px",
                flexShrink: 0
              }}
            >
              <h2 style={{ margin: 0, color: "#fff", fontSize: "22px" }}>Invoice Image</h2>
              <button
                type="button"
                onClick={() => setRequestInvoicePreview(null)}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  border: "none",
                  backgroundColor: "#444",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "18px",
                  lineHeight: "36px"
                }}
              >
                X
              </button>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 0
              }}
            >
              <img
                src={requestInvoicePreview.url}
                alt={requestInvoicePreview.name || "Invoice preview"}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: "10px",
                  border: "1px solid #444",
                  backgroundColor: "#1e2127"
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showInvoiceLargeView && invoicePreviewUrl && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            padding: "25px"
          }}
          onClick={() => setShowInvoiceLargeView(false)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh"
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowInvoiceLargeView(false)}
              style={{
                position: "absolute",
                top: "-16px",
                right: "-16px",
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: "#444",
                color: "#fff",
                cursor: "pointer",
                fontSize: "20px",
                lineHeight: "34px"
              }}
            >
              X
            </button>
            <img
              src={invoicePreviewUrl}
              alt="Invoice large preview"
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "10px",
                border: "1px solid #444",
                backgroundColor: "#1e2127"
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function LicenseRequestTable({
  requests,
  onApproveClick,
  onInvoicePreviewClick,
  canDelete = false,
  onDeleteClick
}) {
  const showApprovedBy = requests.some((request) => isResolvedStatus(request.status));

  const tableColumns = [
    { label: "Requested At", key: "requestedAt" },
    { label: "Generated By", key: "generatedBy" },
    { label: "Invoice Image", key: "invoiceImage" },
    { label: "Comment", key: "comment" },
    { label: "Existing Expiry Date", key: "existingExpiryDate" },
    { label: "New Expiry Date", key: "newExpiryDate" },
    { label: "Request Type", key: "requestType" },
    { label: "Status", key: "status" },
    ...(showApprovedBy ? [{ label: "Approved By", key: "approvedBy" }] : []),
    { label: "Action", key: "action" }
  ];

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        className="no-row-hover"
        style={{
          width: "100%",
          minWidth: "1080px",
          borderCollapse: "collapse",
          backgroundColor: "#1e2127",
          border: "1px solid #444",
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        <thead>
          <tr>
            {tableColumns.map((column) => (
              <th
                key={column.label}
                style={{
                  padding: "13px 14px",
                  color: "#9ca3af",
                  fontSize: "13px",
                  fontWeight: 700,
                  textAlign: "left",
                  borderBottom: "1px solid #444",
                  whiteSpace: "nowrap"
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {requests.map((request) => {
            const isApproved = isApprovedStatus(request.status);

            return (
              <tr
                key={request.id || `${request.requestedAt}-${request.generatedBy}`}
                style={{ backgroundColor: "#1e2127" }}
              >
                <td style={tableCellStyle(false)}>{formatDisplayDateTime(request.requestedAt)}</td>
                <td style={tableCellStyle(false)}>{request.generatedBy || "N/A"}</td>
                <td style={tableCellStyle(false)}>
                  {isUpdateRequestType(request.requestType) ? (
                    <InvoiceImage
                      requestStatus={request}
                      onPreviewClick={onInvoicePreviewClick}
                    />
                  ) : (
                    "N/A"
                  )}
                </td>
                <td style={tableCellStyle(false)}>
                  {getLicenseRequestComment(request)}
                </td>
                <td style={tableCellStyle(false)}>{formatDisplayDate(request.currentExpiryDate)}</td>
                <td style={tableCellStyle(false)}>{formatDisplayDate(request.newExpiryDate)}</td>
                <td style={tableCellStyle(false)}>{normalizeRequestType(request.requestType) || "N/A"}</td>
                <td style={tableCellStyle(true)}>{formatStatusLabel(request.status)}</td>
                {showApprovedBy && (
                  <td style={tableCellStyle(false)}>{formatPersonName(request.approvedBy) || "N/A"}</td>
                )}
                <td style={tableCellStyle(false)}>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => onApproveClick(request)}
                      disabled={isApproved}
                      style={{
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: 700,
                        backgroundColor: isApproved ? "#374151" : "#187718",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        cursor: isApproved ? "not-allowed" : "pointer",
                        opacity: isApproved ? 0.75 : 1
                      }}
                    >
                      {isApproved ? "Approved" : "Approve"}
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => onDeleteClick?.(request)}
                        style={{
                          padding: "8px 16px",
                          fontSize: "14px",
                          fontWeight: 700,
                          backgroundColor: "#dc2626",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer"
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function tableCellStyle(highlight) {
  return {
    padding: "14px",
    color: highlight ? "#22c55e" : "#fff",
    fontSize: "15px",
    fontWeight: highlight ? 700 : 600,
    borderTop: "1px solid #2f333d",
    verticalAlign: "top",
    wordBreak: "break-word"
  };
}

function InvoiceImage({ requestStatus, onPreviewClick }) {
  const thumbSrc = requestStatus.invoiceImageUrl || "";
  const fullSrc = requestStatus.invoiceImageFullUrl || "";
  const [src, setSrc] = useState(fullSrc || thumbSrc);

  useEffect(() => {
    setSrc(fullSrc || thumbSrc);
  }, [fullSrc, thumbSrc]);

  if (!requestStatus.invoiceName) {
    return "Not uploaded";
  }

  if (!src) {
    return "Image unavailable";
  }

  return (
    <button
      type="button"
      title="View invoice image"
      onClick={() =>
        onPreviewClick({
          url: fullSrc || src,
          name: requestStatus.invoiceName
        })
      }
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer"
      }}
    >
      <img
        src={src}
        alt="Invoice"
        onError={() => {
          if (fullSrc && src !== fullSrc) {
            setSrc(fullSrc);
          }
        }}
        style={{
          width: "90px",
          height: "70px",
          objectFit: "cover",
          borderRadius: "6px",
          border: "1px solid #444",
          backgroundColor: "#111827",
          display: "block"
        }}
      />
    </button>
  );
}

function formatDisplayDate(dateString) {
  if (!dateString) {
    return "N/A";
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function formatDisplayDateTime(dateString) {
  if (!dateString) {
    return "N/A";
  }

  return new Date(dateString).toLocaleString();
}

const getNormalizedId = (value) => {
  if (value == null || value === "") {
    return "";
  }

  if (typeof value === "object") {
    if (value.$oid) {
      return String(value.$oid).trim();
    }

    const nested = value._id || value.id || value.branchId || value.companyId;
    if (nested != null && nested !== value) {
      return getNormalizedId(nested);
    }

    if (typeof value.toString === "function" && value.toString !== Object.prototype.toString) {
      const asString = String(value.toString()).trim();
      if (asString && asString !== "[object Object]") {
        return asString;
      }
    }

    return "";
  }

  return String(value).trim();
};

const resolveBranchId = (branchId, branch) => {
  return getNormalizedId(
    branchId ||
    branch?._id ||
    branch?.id ||
    branch?.branchId ||
    branch?.branch_id ||
    branch?.branchID
  );
};

const resolveCompanyId = (companyId, branch) => {
  return getNormalizedId(
    companyId ||
    branch?.company ||
    branch?.companyId ||
    branch?.company_id ||
    branch?.companyID
  );
};

const getApiResponseMessage = (response) => {
  if (typeof response === "string") {
    return response.trim();
  }

  const message =
    response?.message ||
    response?.data?.message ||
    response?.response?.message ||
    response?.error ||
    response?.data?.error ||
    response?.response?.error;

  return typeof message === "string" ? message.trim() : "";
};

const resolveLicenseDetailFromResponse = (response) => {
  if (!response || typeof response !== "object") {
    return null;
  }

  const candidates = [
    response,
    response?.licenseDetail,
    response?.data?.licenseDetail,
    response?.data,
    response?.response
  ];

  return candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      candidate.expiryDate
  ) || null;
};

function normalizeBranchLicense(license) {
  if (!license || typeof license !== "object") {
    return null;
  }

  const token1 = String(license.token1 || "").trim();
  const token2 = String(license.token2 || "").trim();
  const licenseKey = String(license.licenseKey || license.key || "").trim();

  if (!token1 && !token2 && !licenseKey) {
    return null;
  }

  return {
    token1,
    token2,
    licenseKey
  };
}

async function buildInvoiceUploadFile(file) {
  const originalName = String(file?.name || "invoice.png").replace(/[:\\/]/g, "_");
  const baseName = originalName.includes(".") ? originalName : `${originalName}.png`;
  const safeName = baseName.replace(/[^a-zA-Z0-9._-]/g, "_");
  const allowedMimeTypes = new Set([
    "image/jpeg",
    "image/jpg",
    "image/pjpeg",
    "image/png",
    "image/gif",
    "application/pdf"
  ]);

  if (file && allowedMimeTypes.has(file.type)) {
    return new File([file], safeName, {
      type: file.type === "image/jpg" ? "image/jpeg" : file.type
    });
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error("Unable to read invoice image."));
    reader.readAsDataURL(file);
  });

  const jpegDataUrl = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Unable to process invoice image."));
        return;
      }
      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    image.onerror = () => reject(new Error("Unable to process invoice image."));
    image.src = dataUrl;
  });

  const parts = String(jpegDataUrl || "").split(",");
  const binary = atob(parts[1] || "");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], safeName.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
}

function extractLicenseRequestList(response) {
  const candidates = [
    response?.updateLicenseRequests,
    response?.data?.updateLicenseRequests,
    response?.response?.updateLicenseRequests,
    response?.data?.items,
    response?.items,
    response?.data,
    response?.response,
    response
  ];

  const list = candidates.find(Array.isArray);
  if (list) {
    return list;
  }

  const singleRecord = unwrapLicenseRequestRecord(
    response?.updateLicenseRequest ||
    response?.data?.updateLicenseRequest ||
    response?.response?.updateLicenseRequest
  );

  return singleRecord ? [singleRecord] : [];
}

function unwrapLicenseRequestRecord(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return null;
  }

  if (
    record.updateLicenseRequest &&
    typeof record.updateLicenseRequest === "object" &&
    !Array.isArray(record.updateLicenseRequest)
  ) {
    return record.updateLicenseRequest;
  }

  return record;
}

function formatPersonName(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "object") {
    return String(
      value.name ||
      value.userName ||
      value.fullName ||
      value.employeeName ||
      value.firstName ||
      value.username ||
      ""
    ).trim();
  }

  return String(value).trim();
}

function readRawRecordValue(record, keys) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return "";
  }

  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
}

function getRecordValue(record, keys) {
  const source = unwrapLicenseRequestRecord(record);

  if (!source || typeof source !== "object") {
    return "";
  }

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return "";
}

function normalizeRequestType(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "update") {
    return "Update";
  }

  if (["extension", "expention", "expansion"].includes(normalized)) {
    return "Extension";
  }

  if (["expire", "expired", "block", "blocked"].includes(normalized)) {
    return "Expire";
  }

  return String(value || "").trim();
}

function isUpdateRequestType(requestType) {
  return normalizeRequestType(requestType) === "Update";
}

function isExtensionRequestType(requestType) {
  return normalizeRequestType(requestType) === "Extension";
}

function isExpireRequestType(requestType) {
  return normalizeRequestType(requestType) === "Expire";
}

function resolveLicenseRequestsFromResponse(response, companyId, branchId) {
  const requestList = extractLicenseRequestList(response);

  return requestList
    .filter((record) => !isDeletedLicenseRequestRecord(record))
    .filter((record) => matchesBranchRecord(record, branchId, companyId))
    .map((record) => mapLicenseRequestRecord(record, companyId, branchId))
    .sort((first, second) => getRequestTimestamp(second) - getRequestTimestamp(first));
}

function isDeletedLicenseRequestRecord(record) {
  const source = unwrapLicenseRequestRecord(record);
  const value = source?.isDelete ?? source?.is_delete ?? source?.deleted;
  if (value === true || String(value).trim().toLowerCase() === "true") {
    return true;
  }

  return isDeletedStatus(source?.status);
}

function isDeletedStatus(status) {
  return String(status || "").trim().toLowerCase() === "deleted";
}

function getRecordId(value) {
  return getNormalizedId(value);
}

function matchesBranchRecord(record, branchId, companyId) {
  const source = unwrapLicenseRequestRecord(record);
  const recordBranchId = getRecordId(source?.branch || source?.branchId || source?.branch_id);

  if (recordBranchId && recordBranchId !== branchId) {
    return false;
  }

  if (!companyId) {
    return true;
  }

  const recordCompanyId = getRecordId(source?.company || source?.companyId || source?.company_id);
  if (!recordCompanyId) {
    return true;
  }

  return recordCompanyId === companyId;
}

function mapLicenseRequestRecord(record, companyId, branchId) {
  const source = unwrapLicenseRequestRecord(record);
  const resolvedCompanyId =
    getRecordId(source?.company || source?.companyId || source?.company_id) ||
    companyId;
  const resolvedBranchId =
    getRecordId(source?.branch || source?.branchId || source?.branch_id) ||
    branchId;
  const invoiceFileName = getRecordValue(source, ["invoiceImage", "invoice_image"]);
  const imageCompanyId = companyId || resolvedCompanyId;
  const imageBranchId = branchId || resolvedBranchId;

  return {
    id: getRecordId(source?._id || source?.id),
    status: getRecordValue(source, ["status"]) || "pending",
    requestType: normalizeRequestType(
      getRecordValue(source, ["requestType", "request_type", "type"])
    ),
    currentExpiryDate: toDateInputValue(
      getRecordValue(source, [
        "existingExpiryDate",
        "existing_expiry_date",
        "currentExpiryDate"
      ])
    ),
    newExpiryDate: toDateInputValue(
      getRecordValue(source, ["newExpiryDate", "new_expiry_date"])
    ),
    generatedBy: getRecordValue(source, ["generatedBy", "generated_by"]),
    requestedAt: getRecordValue(source, [
      "requestedAt",
      "requested_at",
      "createdDate",
      "createdAt"
    ]),
    invoiceName: invoiceFileName,
    invoiceImageUrl: buildInvoiceImageUrl(
      imageCompanyId,
      imageBranchId,
      invoiceFileName,
      true
    ),
    invoiceImageFullUrl: buildInvoiceImageUrl(
      imageCompanyId,
      imageBranchId,
      invoiceFileName,
      false
    ),
    extensionComment: getRecordValue(source, [
      "extensionComment",
      "extension_comment",
      "comment"
    ]),
    updateComment: getRecordValue(source, [
      "updateComment",
      "update_comment",
      "Update Comment"
    ]),
    approvedBy: formatPersonName(
      getRecordValue(source, ["approvedBy", "approved_by", "Approved By"]) ||
      readRawRecordValue(record, ["approvedBy", "approved_by", "Approved By"])
    )
  };
}

function getLicenseRequestComment(request) {
  if (isExtensionRequestType(request?.requestType)) {
    return request?.extensionComment || "N/A";
  }

  if (isUpdateRequestType(request?.requestType)) {
    return request?.updateComment || request?.extensionComment || "N/A";
  }

  return request?.updateComment || request?.extensionComment || "N/A";
}

function buildInvoiceImageUrl(companyId, branchId, fileName, useThumb = false) {
  if (!fileName) {
    return "";
  }

  const rawName = String(fileName).trim();
  if (rawName.startsWith("data:") || rawName.startsWith("http://") || rawName.startsWith("https://")) {
    return rawName;
  }

  const diskFileName = rawName.includes(":") ? rawName.split(":")[0] : rawName;
  if (!diskFileName || !companyId || !branchId) {
    return "";
  }

  const folder = useThumb
    ? `company_${companyId}/branch_${branchId}/licenseInvoices/thumbs`
    : `company_${companyId}/branch_${branchId}/licenseInvoices`;

  return `${getLocalServerURL()}/assets/layout/images/${folder}/${encodeURI(diskFileName).replace(/#/g, "%23")}`;
}

function toDateInputValue(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().split("T")[0];
}

function getRequestTimestamp(request) {
  const timestamp = new Date(request?.requestedAt || 0).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function isApprovedStatus(status) {
  return String(status || "").trim().toLowerCase() === "approved";
}

function isExpiredStatus(status) {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus === "expire" || normalizedStatus === "expired" || normalizedStatus === "block";
}

function isResolvedStatus(status) {
  return isApprovedStatus(status) || isExpiredStatus(status);
}

function formatStatusLabel(status) {
  const normalizedStatus = String(status || "").trim().toLowerCase();

  if (normalizedStatus === "approved") {
    return "Approved";
  }

  if (["expire", "expired", "block"].includes(normalizedStatus)) {
    return "Expire";
  }

  if (normalizedStatus === "pending") {
    return "Pending";
  }

  return status || "Pending";
}

const licenseRequestBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "22px",
  height: "22px",
  marginLeft: "8px",
  padding: "0 6px",
  borderRadius: "999px",
  backgroundColor: "#ef4444",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 800,
  lineHeight: 1
};

export default UpdateLicense;
